import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FullLogo, SOCIALS, SocialMark } from "./Navbar";

export default function Footer() {
  const location = useLocation();

  const trackSocialClick = (platform) => {
    if (!window.gtag) return;

    window.gtag("event", "social_link_click", {
      platform,
      link_location: "footer",
    });
  };
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <footer className="bg-ink text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FullLogo className="h-12 w-auto" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-cream/60">
              A Toronto production company making original films and providing full service production — with the communities that inspire the work.
            </p>
            <p className="mt-5 font-display text-lg font-extrabold tracking-tight text-cream" data-testid="footer-slogan">
              Think <span className="text-orange">Incolour</span>.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-orange px-6 py-3 text-sm font-semibold text-cream transition-transform hover:scale-[1.03]"
            >
              Start a project <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-3">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-cream/40">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link className="text-cream/80 transition-colors hover:text-orange" to="/original" data-testid="footer-link-original">Original Work</Link></li>
              <li><Link className="text-cream/80 transition-colors hover:text-orange" to="/service" data-testid="footer-link-service">Service Production</Link></li>
              <li><Link className="text-cream/80 transition-colors hover:text-orange" to="/about" data-testid="footer-link-about">About</Link></li>
              <li><Link className="text-cream/80 transition-colors hover:text-orange" to="/journal" data-testid="footer-link-journal">Journal</Link></li>
              <li><Link className="text-cream/80 transition-colors hover:text-orange" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-4">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-cream/40">Studio</p>
            <address className="mt-5 space-y-2 text-sm not-italic text-cream/80">
              <p>Toronto &amp; The GTA</p>
              <p><a href="mailto:connect@incolourfilms.ca" onClick={() => window.gtag && window.gtag("event", "email_click", { source: "footer" })} className="transition-colors hover:text-orange" data-testid="footer-email-link">connect@incolourfilms.ca</a></p>
            </address>
            <div className="mt-6 flex items-center gap-2" data-testid="footer-socials">
              {SOCIALS.map(({ href, label, id, Icon }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  onClick={() => trackSocialClick(id)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/15 text-cream/70 transition-colors hover:border-cream/30 hover:bg-cream/10 hover:text-orange"
                  data-testid={`footer-social-${id}`}
                >
                  <SocialMark id={id} Icon={Icon} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-cream/10 pt-6 text-xs font-mono uppercase tracking-[0.2em] text-cream/40 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Incolour Films Inc. — Toronto</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="transition-colors hover:text-orange" data-testid="footer-privacy-link">Privacy</Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("incolour:analytics-settings"))}
              className="transition-colors hover:text-orange"
              data-testid="footer-analytics-settings"
            >
              Analytics settings
            </button>
            <Link to="/admin/login" rel="nofollow" className="transition-colors hover:text-orange" data-testid="footer-cms-link">Team CMS Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
